(ns storrito.connect3.info-panel
  (:require [clojure.string :as str]
            [storrito.calendly.links :as calendly]
            [ui.user-session :as user-session]))

(defn- warning-item
  [icon-class & content]
  [:li.flex.items-start.gap-3
   [:div.w-5.h-5.mt-0.5.flex-shrink-0.flex.items-center.justify-center
    [:i {:class icon-class}]]
   (into [:span.text-gray-700] content)])

(def tips
  [{:icon "fas fa-users" :section :info :content [[:b "Inform everyone"] " with account access " [:b "not to deny the new device login."]]}
   {:icon "fas fa-lock" :section :info :content ["We recommend enabling " [:b "2FA"] " beforehand, preferably via an " [:b "authenticator app."]]}
   {:icon "fas fa-arrow-right" :section :info :content [[:b "Approve"] " any new login prompts from Instagram."]}
   {:icon "fas fa-robot" :section :info :content [[:b "Automation warnings"] " from Instagram are normal and " [:b "ok in most cases."]]}
   {:icon "fas fa-key" :section :warning :content ["Changing your " [:b "Instagram password"] " will require a reconnect."]}
   {:icon "fas fa-shield-alt" :section :warning :content ["Enabling " [:b "2FA after"] " connecting will break the connection."]}
   {:icon "fas fa-ban" :section :warning :content ["Denying the " [:b "new device login"] " will drop the connection."]}
   {:icon "fas fa-at" :section :warning :content ["Renaming your " [:b "Instagram account"] " will require a reconnect."]}
   {:icon "fas fa-bolt" :section :warning :content ["Posting " [:b "too many stories"] " at once may cause Instagram to revoke access."]}
   {:icon "fas fa-eye-slash" :section :warning :content ["Posting " [:b "adult or NSFW content"] " may cause Instagram to revoke access."]}])

(def ^:private section-config
  {:info {:title "Good to know"
          :icon-color "text-blue-500"
          :box-class "bg-blue-50 border-blue-200 mb-6"}
   :warning {:title "Keep in mind"
             :icon-color "text-amber-500"
             :box-class "bg-amber-50 border-amber-200"}})

(defn- tips-section [section-key items]
  (let [{:keys [title icon-color box-class]} (section-config section-key)]
    [:div.border.rounded-lg.p-5 {:class box-class}
     [:h3.font-semibold.text-gray-900.mb-3 title]
     (into [:ul.space-y-3]
           (map (fn [{:keys [icon content]}]
                  (apply warning-item (str icon " " icon-color) content))
                items))]))

(defn connect-query
  "The query string of the connect3 URL. `source` names the place the
   connect was started from (welcome screen, account selector, ...): the
   connect3 page keeps its query, so every `connect3/opened` log row
   carries it in the referer. 52 % of the sessions that reach the login
   form never submit a password (16.–27.09.2026) and nothing told from
   where those sessions came."
  [{:keys [username reconnect source]}]
  (let [params (cond-> []
                 username (conj (str "username=" (js/encodeURIComponent username))
                                (str "reconnect=" reconnect))
                 source (conj (str "source=" (js/encodeURIComponent source))))]
    (when (seq params)
      (str "?" (str/join "&" params)))))

(defn connect-url [params]
  (str "/ui/connect3/" (random-uuid) (connect-query params)))

(defn- ls-key [params]
  (if (:reconnect params)
    "connect3-info-dismissed-reconnect"
    "connect3-info-dismissed-connect"))

(defn dismissed? [params]
  (= "true" (.getItem js/localStorage (ls-key params))))

(defn dismiss! [params]
  (.setItem js/localStorage (ls-key params) "true"))

(defn tips-and-warnings []
  (let [by-section (group-by :section tips)]
    [:div
     (tips-section :info (:info by-section))
     (tips-section :warning (:warning by-section))]))

(defn render
  [state]
  (let [params (get-in state [:ui/modal :params])
        dont-show? (:connect3-info/dont-show state)]
    [:div
     [:div.flex.justify-center.mb-6
      [:div.w-16.h-16.bg-blue-100.rounded-full.flex.items-center.justify-center
       [:i.fas.fa-info-circle.text-blue-500.text-2xl]]]

     [:h2.text-2xl.font-bold.text-gray-900.mb-2.text-center
      "Quick tips for a smooth connection"]

     [:p.text-gray-500.text-center.mb-6
      "A few things to keep in mind to stay connected."]
     (tips-and-warnings)

     [:label.flex.items-center.gap-2.mt-6.cursor-pointer.text-sm.text-gray-600
      [:input.h-4.w-4.rounded.border-gray-300.text-blue-600.cursor-pointer
       {:type "checkbox"
        :checked (boolean dont-show?)
        :on {:change [[:store/assoc-in [:connect3-info/dont-show] (not dont-show?)]]}}]
      "Don't show this again"]

     [:button.block.w-full.px-6.py-3.mt-4.bg-blue-600.text-white.text-center.rounded-lg.font-semibold.text-lg.hover:bg-blue-700.transition-all.duration-200.shadow-md.hover:shadow-lg.cursor-pointer
      {:on {:click [[:connect3-info/continue params]]}}
      "I understand, continue"]

     [:a.block.w-full.mt-3.text-center.text-sm.text-blue-600.hover:underline
      {:href (calendly/url :connect-help "connect-tips-modal" (user-session/org-uuid state))
       :target "_blank"}
      "Book a 15-minute connect call"]]))

(defn- continue!
  "Saves dismiss preference, opens the connect URL in a new tab, and closes
   the modal."
  [{:keys [store action]}]
  (let [params (nth action 1)
        dont-show? (:connect3-info/dont-show @store)]
    (when dont-show?
      (dismiss! params))
    (.open js/window (connect-url params) "_blank")
    (swap! store dissoc :ui/modal :connect3-info/dont-show)))

(defn- open-or-navigate!
  "Opens the info modal, or navigates directly if the user dismissed it."
  [{:keys [action ui/event-handler]}]
  (let [params (nth action 1)]
    (if (dismissed? params)
      (.open js/window (connect-url params) "_blank")
      (event-handler {} [[:ui/modal-open :modals/connect3-info params]]))))

(def modal
  {:modal-id :modals/connect3-info
   :render #'render})

(defn- reset-dismissed!
  "Removes both localStorage keys so the info modal shows again."
  [_]
  (.removeItem js/localStorage "connect3-info-dismissed-connect")
  (.removeItem js/localStorage "connect3-info-dismissed-reconnect"))

(def register
  [modal
   {:ui.action/kind :connect3-info/continue
    :ui.action/handler #'continue!}
   {:ui.action/kind :connect3-info/open
    :ui.action/handler #'open-or-navigate!}
   {:ui.action/kind :connect3-info/reset-dismissed
    :ui.action/handler #'reset-dismissed!}])
